import { Component, OnInit, ViewChild } from '@angular/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { isNotEmpty } from 'src/app/extensions/utilities';
import { SIGNAL_ITEM_KEY } from 'src/app/extensions/constants/commonConstants';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { forkJoin, of } from 'rxjs';
import { NgForm } from '@angular/forms';
import { SPECIAL_OFFER_ROUTES } from 'src/app/core/constants/routes-constants';

@Component({
  selector: 'app-special-offers',
  templateUrl: './client-segmentation.component.html',
  styleUrls: ['./client-segmentation.component.scss'],
  standalone: false,
})
export class ClientSegmentationComponent implements OnInit {
  @ViewChild('form') public form: NgForm;
  private stateSignalItem: any;
  canUnloadByPassForm: boolean = false;

  public specialOffer: app.models.GetSpecialOfferModel = {
    title: '',
    campaignDocuments: [],
  } as app.models.GetSpecialOfferModel;

  public segmentTypes: app.models.DictionaryDataModel[];
  public segmentItems: app.models.DictionaryDataModel[];
  selectedSegmentItems: app.models.DictionaryDataModel[];
  specialOfferId: string;

  constructor(
    private readonly _globalService: GlobalService,
    private readonly _router: Router,
    private readonly dictionaryAPIService: DictionaryAPIService,
    private _activatedRoute: ActivatedRoute
  ) {}

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';

  public segmentationToggle: {
    segment: boolean;
    maximumPaymentDelay: boolean;
    periodUntilTermination: boolean;
  } = {
    segment: false,
    maximumPaymentDelay: false,
    periodUntilTermination: false,
  };

  ngOnInit(): void {
    this.specialOfferId = this._activatedRoute.snapshot.params['specialOfferId'];
    this.stateSignalItem = this._globalService.getSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA);
    if (isNotEmpty(this.stateSignalItem)) {
      this.specialOffer = this.stateSignalItem.data as app.models.GetSpecialOfferModel;
      this.specialOffer.dictionaryCampaignTypeId = this.specialOffer.dictionaryCampaignTypeId.toUpperCase();
    } else {
      this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
      if (this.specialOfferId) {
        this._router.navigate([
          SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
          SPECIAL_OFFER_ROUTES.PREVIEW,
          this.specialOfferId,
        ]);
      } else {
        this.firstStepRedirectHandler();
      }
    }

    this.loadData();
    if (this.specialOffer.dictionarySegmentItemTypeExternalEntityId) {
      this.segmentationToggle.segment = true;
    }
    if (this.specialOffer.maximumPaymentDelay) {
      this.segmentationToggle.maximumPaymentDelay = true;
    }
    if (this.specialOffer.periodUntilTermination) {
      this.segmentationToggle.periodUntilTermination = true;
    }
  }

  loadData(): void {
    const segmentTypes$ = this.dictionaryAPIService.getDictionarySegmentTypes();
    const segmentItems$ = this.specialOffer.dictionarySegmentItemTypeExternalEntityId
      ? this.dictionaryAPIService.getDictionarySegmentItems(this.specialOffer.dictionarySegmentItemTypeExternalEntityId)
      : of(null);

    let source = forkJoin([segmentTypes$, segmentItems$]);
    source.subscribe(([segmentTypes, segmentItems]) => {
      this.segmentTypes = segmentTypes;
      if (segmentItems) {
        this.segmentItems = segmentItems;
        this.selectedSegmentItems = segmentItems.filter(segment =>
          this.specialOffer.segmentItems.some(x => x.dictionarySegmentItemExternalEntityId === segment.id)
        );
      }
    });
  }

  changeSegmentFilterHandler(event: boolean) {
    if (!event) {
      this.specialOffer.dictionarySegmentItemTypeExternalEntityId = null;
      this.specialOffer.segmentItemType = null;
      this.selectedSegmentItems = null;
      this.specialOffer.segmentItems = null;
    }
  }

  changeMaximumPaymentDelayHandler(event: any) {
    if (!event) this.specialOffer.maximumPaymentDelay = null;
  }

  changePeriodUntilTerminationHandler(event: any) {
    if (!event) this.specialOffer.periodUntilTermination = null;
  }

  redirectToPrevStep() {
    this.canUnloadByPassForm = true;
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.CAMPAIGN_DESCRIPTION,
      this.specialOfferId ?? '',
    ]);
  }

  firstStepRedirectHandler = () => {
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.CAMPAIGN_DETAILS,
      this.specialOfferId ?? '',
    ]);
  };

  segmentTypeSelectedHandler(event: string) {
    this.specialOffer.segmentItems = null;
    this.selectedSegmentItems = null;
    if (!event) {
      this.specialOffer.segmentItemType = null;
    } else {
      this.specialOffer.segmentItemType = this.segmentTypes.find(x => x.id === event).value;
      this.dictionaryAPIService.getDictionarySegmentItems(event).subscribe(segmentItems => {
        this.segmentItems = segmentItems;
      });
    }
  }

  campaignTypeSelectedHandler(event: app.models.DictionaryDataModel[]) {
    if (!event) this.specialOffer.campaignType = null;
    else {
      this.specialOffer.segmentItems = event.map(item => {
        const match = this.specialOffer.segmentItems?.find(
          ref => ref.dictionarySegmentItemExternalEntityId === item.id
        );
        return {
          specialOfferXSegmentItemId: match ? match.specialOfferXSegmentItemId : '',
          dictionarySegmentItemExternalEntityId: item.id,
          dictionarySegmentItemName: item.value,
        };
      });
    }
  }

  nextStepHandler() {
    this.canUnloadByPassForm = true;
    this.stateSignalItem = {
      data: { ...this.stateSignalItem.data, ...this.specialOffer, isClientSegmentaionFormDirty: this.form.dirty },
    };
    this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, this.stateSignalItem);
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.PREVIEW,
      this.specialOfferId ?? '',
    ]);
  }
}
